import React, { useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { FloatTextArea } from 'ui-components/components/float-text-area';
import { Text, TextType } from 'ui-components/components/text';

import styles from './labs-feedback-modal.module.less';
import { messages } from './messages';
import { SimpleModal } from '../../../../../components/simple-modal';
import { useLabsFeedback } from '../../hooks/use-labs-feedback';
import { featureMessages } from '../../labs-feature-messages';
/**
 * Modal component for collecting user feedback on lab features
 * @param {object} props - Component props
 * @param {() => void} props.onClose - Function to close the modal
 * @param {import('../../typedefs').LabsFeature} props.feature - The feature object being given feedback on
 * @param {boolean} [props.isDisableMode] - Whether modal is in disable mode (shows disable-specific text and disable button)
 * @param {boolean} [props.isLoading] - Whether API call is in progress
 * @param {(feature: import('../../typedefs').LabsFeature, feedback: string) => Promise<void>} [props.onDisableFeature] - Function to disable feature
 * @returns {React.ReactElement|null} The feedback modal component
 */
export const LabsFeedbackModal = ({ onClose, feature, isDisableMode = false, isLoading = false, onDisableFeature }) => {
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { saveFeedback } = useLabsFeedback();

  const handleSubmit = useCallback(async () => {
    const trimmedFeedback = feedbackText.trim();

    if (isDisableMode && onDisableFeature) {
      await onDisableFeature(feature, trimmedFeedback);
      return;
    }

    if (!trimmedFeedback) {
      return;
    }

    setIsSubmitting(true);
    try {
      saveFeedback({
        feature,
        feedbackText: trimmedFeedback,
        isDisableMode,
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  }, [isDisableMode, onDisableFeature, feature, feedbackText, saveFeedback, onClose]);

  const titleMessage = isDisableMode ? messages.disableFeedbackTitle : messages.feedbackTitle;
  const descriptionMessage = isDisableMode ? messages.disableFeedbackDescription : messages.feedbackDescription;
  const actionButtonTheme = isDisableMode ? 'danger' : 'primary';
  const actionMessage = isDisableMode ? messages.disable : messages.submit;
  const isActionDisabled = isDisableMode ? isLoading : !feedbackText.trim() || isSubmitting;

  return (
    <SimpleModal
      actionButtonTheme={actionButtonTheme}
      actionMessage={actionMessage}
      bodyClassName={styles.modalBody}
      cancelMessage={messages.cancel}
      className={styles.feedbackModal}
      headerMessage={titleMessage}
      isActionDisabled={isActionDisabled}
      onAction={handleSubmit}
      onCancel={onClose}
      onClose={onClose}
      size='small'
    >
      <>
        <Text
          formattedMessageValues={{
            featureName: <FormattedMessage {...featureMessages[feature?.titleKey]} />,
          }}
          textType={TextType.Description}
          value={descriptionMessage}
        />

        <FloatTextArea
          className={styles.feedbackTextArea}
          label={<FormattedMessage {...messages.feedbackPlaceholder} />}
          maxLength={200}
          onChange={setFeedbackText}
          rows={4}
          value={feedbackText}
        />
      </>
    </SimpleModal>
  );
};
